/**
 * Mounts the four "create new …" dialogs the header `Create ▾` menu opens.
 *
 * Each dialog already exists and owns its own form/mutation — this component
 * only decides *which one* is open (via `CreateEntityDialogsContext`) and
 * where to land once creation succeeds: the entity's list route, since the
 * header can be triggered from anywhere in the app, not just that list page.
 *
 * Every dialog (and the manufacturer dialog's prefs/permission queries) is
 * only mounted once its kind is actually active, so none of the four add
 * weight to the initial bundle or fire a network request until the user
 * opens that specific "New …" item.
 */
import React, { Suspense, lazy } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateEntityDialogs } from './CreateEntityDialogsContext'

const ProjectCreateDialog = lazy(() =>
  import('@/features/projects/components/ProjectCreateDialog').then((m) => ({
    default: m.ProjectCreateDialog,
  })),
)
const CompanyFormDialog = lazy(() =>
  import('@/features/contacts/components/CompanyFormDialog').then((m) => ({
    default: m.CompanyFormDialog,
  })),
)
const AddProductModal = lazy(() => import('@/features/inventory/components/editor/AddProductModal'))
const ManufacturerCreateDialog = lazy(() =>
  import('@/features/manufacturer-settings/components/ManufacturerCreateDialog').then((m) => ({
    default: m.ManufacturerCreateDialog,
  })),
)

export function GlobalCreateDialogs(): JSX.Element {
  const { active, close } = useCreateEntityDialogs()
  const navigate = useNavigate()

  return (
    <Suspense fallback={null}>
      {active === 'project' && (
        <ProjectCreateDialog
          open
          onClose={close}
          onCreated={() => {
            close()
            navigate('/projects')
          }}
        />
      )}

      {active === 'contact' && (
        <CompanyFormDialog
          open
          companyId={null}
          onClose={close}
          onSaved={() => {
            close()
            navigate('/contacts')
          }}
        />
      )}

      {active === 'product' && (
        <AddProductModal
          open
          productId={null}
          onClose={close}
          onSaved={() => {
            close()
            navigate('/inventory')
          }}
        />
      )}

      {active === 'manufacturer' && <ManufacturerCreateDialog onClose={close} />}
    </Suspense>
  )
}
